// Entry point — boots the app, owns the sidebar and tab router.
import { store } from './state';
import { getWorkerBase } from './worker';
import { SIDEBAR_NAV, getTab } from './tabs/registry';
import { renderAgentTeaser } from './tabs/agent-teaser';
import { openSettings, saveToKV } from './settings';
import { showSplash } from './splash';
import { initWatchlist } from './watchlist';
import { requireAuth } from './auth';

// ── Sidebar ───────────────────────────────────────────────────────────────────

function getSidebarCollapsed(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem('meridian.sb.collapsed') ?? '{}'); }
  catch { return {}; }
}

function renderSidebar(): void {
  const sb = document.getElementById('sidebar');
  if (!sb) return;

  const active = store.get('activeTab');
  const collapsed = getSidebarCollapsed();

  sb.innerHTML = `
    <div class="sb-brand">
      <svg class="sb-logo-icon"><use href="#i-logo"/></svg>
      <span class="sb-logo-text">MERIDIAN</span>
      <span class="sb-v">v6.0</span>
      <button class="sb-nav-toggle" id="sb-toggle" title="Toggle sidebar">
        <svg><use href="#i-chev"/></svg>
      </button>
    </div>`;

  for (const grp of SIDEBAR_NAV) {
    const isCollapsed = collapsed[grp.group] ?? false;
    const g = document.createElement('div');
    g.className = `sb-group${isCollapsed ? ' collapsed' : ''}`;
    g.innerHTML = `<button class="sb-group-hd">${grp.group}<svg class="sb-chev"><use href="#i-chev"/></svg></button><div class="sb-items"></div>`;

    g.querySelector('.sb-group-hd')!.addEventListener('click', () => {
      collapsed[grp.group] = !collapsed[grp.group];
      localStorage.setItem('meridian.sb.collapsed', JSON.stringify(collapsed));
      renderSidebar();
    });

    const box = g.querySelector('.sb-items')!;
    for (const item of grp.items) {
      const b = document.createElement('button');
      b.className = `sb-item${item.id === active ? ' active' : ''}`;
      b.dataset.sid = item.id;
      b.innerHTML = `<svg><use href="#${item.icon}"/></svg><span>${item.label}</span>`;
      b.addEventListener('click', () => showTab(item.id));
      box.appendChild(b);
    }
    sb.appendChild(g);
  }

  document.getElementById('sb-toggle')?.addEventListener('click', toggleSidebar);
  renderAgentTeaser(sb, () => showTab('agentops'));

}

function toggleSidebar(): void {
  const sb = document.getElementById('sidebar');
  const isCollapsed = !localStorage.getItem('meridian.sb.navCollapsed');
  sb?.classList.toggle('nav-collapsed', isCollapsed);
  if (isCollapsed) localStorage.setItem('meridian.sb.navCollapsed', '1');
  else localStorage.removeItem('meridian.sb.navCollapsed');
}

// ── Tab router ────────────────────────────────────────────────────────────────

const mountedTabs = new Set<string>();

// Tabs not in the sidebar that should highlight a parent sidebar item
const SIDEBAR_ALIAS: Record<string, string> = {
  'scanner': 'charts',
  'signals': 'charts',
};

export function showTab(id: string): void {
  if (store.get('activeTab') === id && mountedTabs.has(id)) return;

  store.set('activeTab', id);
  if (location.hash.slice(1) !== id) history.replaceState(null, '', '#' + id);  // keep URL deep-linkable (replaceState = no history spam)

  document.querySelectorAll<HTMLElement>('.section').forEach(s => s.classList.remove('active'));
  document.getElementById(`tab-${id}`)?.classList.add('active');
  const sidebarId = SIDEBAR_ALIAS[id] ?? id;
  document.querySelectorAll<HTMLElement>('.sb-item').forEach(b =>
    b.classList.toggle('active', b.dataset.sid === sidebarId),
  );

  const tab = getTab(id);
  if (tab) {
    if (!mountedTabs.has(id)) { tab.init(); mountedTabs.add(id); }
    tab.render();
  }

  // Sync Charts/Scanner/Signals sub-nav highlight across all three sections
  const subMap: Record<string, string> = { charts: 'chart', scanner: 'scanner', signals: 'signals' };
  const subKey = subMap[id];
  if (subKey) {
    ['charts', 'scanner', 'signals'].forEach(prefix => {
      ['chart','scanner','signals'].forEach(k => {
        document.getElementById(`${prefix}-sub-${k}`)?.classList.toggle('active', k === subKey);
      });
    });
  }

  localStorage.setItem('meridian.lastTab', id);
}

// ── Boot ──────────────────────────────────────────────────────────────────────

function boot(): void {
  // Allow ?worker=URL to configure the worker endpoint on the fly
  const qWorker = new URLSearchParams(window.location.search).get('worker');
  if (qWorker) localStorage.setItem('meridian_worker_url', qWorker);

  if (localStorage.getItem('meridian.sb.navCollapsed'))
    document.getElementById('sidebar')?.classList.add('nav-collapsed');

  renderSidebar();

  // Deep-link: #<tabid> wins over the last-used tab so shared links land on the right tab
  const hashTab = location.hash.replace(/^#/, '');
  const startTab = (hashTab && getTab(hashTab)) ? hashTab : (localStorage.getItem('meridian.lastTab') ?? 'tracker');
  showTab(startTab);

  // React to hash changes (a shared link opened while the app is already running, or back/forward)
  window.addEventListener('hashchange', () => {
    const id = location.hash.replace(/^#/, '');
    if (id && getTab(id) && store.get('activeTab') !== id) showTab(id);
  });

  // Expose for legacy compat and claude.ai MCP calls during migration
  (window as any).showTab      = showTab;
  (window as any).openSettings = openSettings;

  // Ctrl+Shift+, opens Settings (nothing visible in UI)
  // Use e.code not e.key — Shift changes ',' to '<' in key value
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'Comma') {
      e.preventDefault();
      openSettings();
    }
  });

  initWatchlist();

  const base = getWorkerBase();
  console.log(`[meridian] v6.0 | worker: ${base || '(not configured)'} | Ctrl+Shift+, for settings`);
}

document.addEventListener('DOMContentLoaded', () =>
  showSplash().then(requireAuth).then(boot).then(() => saveToKV()),
);
